<script setup>
import { ref, watch, onMounted,  computed, defineExpose, getCurrentInstance } from "vue";
import {  RouterLink, useRouter } from 'vue-router'
import Modal from "../../../../components/ui-components/modal/Modal.vue";
import {usePageLoadingStore} from "../../../../stores/pageLoading";
import adminCorpInfoService from "@/pages/admin/services/corporation/adminCorpInfoService";
import adminMatchingService from "@/pages/admin/services/matching/adminMatchingService";
import { useAlertStore } from "@/stores/alertStore";
const {$alert, $confirm} = useAlertStore();

const loadingStore = usePageLoadingStore()
const { $pageListBoardNo, $toast, $yyyymmddHHmmss, $phoneNum } = getCurrentInstance().appContext.config.globalProperties;

const router = useRouter();

const list = ref([]);
const currentPage = ref(1);
const pageSize = ref(10);
const totalPages = ref(1);
const totalCount = ref(0);

const baseModal = ref(null);
const resolvePromise = ref(null);

const corpIds = ref('');

onMounted(()=>{
  setTimeout(()=>{
    baseModal.value.isAdminWrap = true;
  }, 500);
})

// 마스터 회원 조회
const retrieveList = () => {
  if (!corpIds.value) {
    return;
  }

  let paramObj = {
    pageNumber: currentPage.value,
    size: pageSize.value,
    corpId: corpIds.value,
  }

  loadingStore.loading = true;
  adminCorpInfoService.retrieveCorpEmpListPaging(paramObj)
      .then( (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          // console.log(result);
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
      }).catch( (error) =>  {
    loadingStore.loading = false;
    console.log(error);
  });
}

const show = () => {
  baseModal.value.open();
  return new Promise((resolve, _) => {
    resolvePromise.value = resolve;
  });
};

const confirm = () => {
  baseModal.value.close();
  resolvePromise.value(true);
};

const cancel = () => {
  baseModal.value.close();
  resolvePromise.value(false);
};

const init = (corpId) => {
  corpIds.value = corpId ? String(corpId) : '';
  currentPage.value = 1;
  retrieveList();
}

const onSelectedPage = (pageNo) => {
  currentPage.value = pageNo;
  retrieveList();
}

const pageSizeGroup = ref([
  { label: "10개씩 보기", value: "10" },
  { label: "30개씩 보기", value: "30" },
  { label: "50개씩 보기", value: "50" },
  { label: "100개씩 보기", value: "100" }
]);


const onSelectPageSize = (option) => {
  pageSize.value = option.value;
  currentPage.value = 1;
  retrieveList();
};


// 마스터 권한 변경
const changeAdminCorpMasterYn = async (userId, masterYn) => {

  let ok;
  if(masterYn === 'Y') {
    ok = await $confirm(["회원에게 마스터 권한을 부여할까요?", "마스터 회원은 일자리/장비 매칭을 할 수 있습니다."], "권한 부여");
  }

  if(!ok) {
    return -1;
  } else {
    let paramObj = {
      userId: userId,
      masterYn: masterYn,
    }
    loadingStore.loading = true;
    adminMatchingService.updateAdminCorpMasterYn(paramObj)
        .then(async (res) => {
          loadingStore.loading = false;
          if (res.data.code.toLowerCase() === 'success') {
            await $alert(["마스터 권한 변경을 하였습니다."]);
            baseModal.value.close();
            resolvePromise.value?.(true);
            router.push({
              name: 'CorporationInfoUpdate',
              params: { id: corpIds.value },
              query: { mode: 'create' },
            });
          } else {
            console.log(res);
          }
        }).catch( (res) => {
      console.log(res)
    });
  }
}

// 회원 상세 새창
const goMemberDetail = (userId) => {
  window.open(`/admin/member/detail/${userId}`, '_blank');
}

defineExpose({
  show,
  init,
})
</script>

<template>
  <Modal ref="baseModal">
    <div class="modal-inner xl-size">
      <button class="close" @click="cancel"></button>
      <div class="modal-title">
        <h3>마스터 지정</h3>
      </div>
      <div class="modal-body">
        <div class="list-top">
          <p>전체 <span>{{ $number(totalCount)}}</span>명</p>
          <div class="grid">
            <KSASelect ref="searchPageSizeRef" :options="pageSizeGroup" class="select ml-xs" @selectOption="onSelectPageSize" />
          </div>
        </div>
        <div class="table-form">
          <table class="horizontal">
            <tr>
              <th class="text-center">NO</th>
              <th class="text-center">가입일</th>
              <th class="text-center">최근 접속일</th>
              <th class="text-center">재직자명</th>
              <th class="text-center">아이디</th>
              <th class="text-center">핸드폰번호</th>
              <th class="text-center">이메일</th>
              <th class="text-center">마스터 권한 변경</th>
            </tr>
            <tr v-for="corp in list">
              <td class="text-center">{{ corp.boardNo }}</td>
              <td>{{ $yyyymmddHHmmss(corp.createDate) }}</td>
              <td>{{ $yyyymmddHHmmss(corp.lastLoginDate) }}</td>
              <td class="link"  @click="goMemberDetail(corp.userId)">{{ corp.userName }}</td>
              <td class="text-center">{{ corp.userLoginId }}</td>
              <td class="text-center">{{ $phoneNum(corp.phoneNum) }}</td>
              <td class="text-center">
                <span v-if="corp.email">{{ corp.email }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-center">
                <span @click="changeAdminCorpMasterYn(corp.userId, 'Y')" class="link text">[마스터 권한 부여]</span>
              </td>
            </tr>
            <tr v-if="list.length === 0">
              <td class="text-center" colspan="8">
                수강 신청한 강의가 없습니다.
              </td>
            </tr>
          </table>
        </div>
        <Paginate
            v-if="list.length > 0"
            v-model="currentPage"
            :page-count="totalPages"
            :click-handler="onSelectedPage"
        />
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
@import "../../../../assets/scss/theme";

.modal-inner {
  width: 598px !important;
}

p {
  margin-bottom: 0;
}

.list-top {
  p {
    margin-right: 0;
    padding-left: 2px;
    &:first-of-type {
      padding-left: 0;
    }
  }
}

.grid {
  &.half {
    div { width: 312px; }
  }
}

.modal-title p span {
  color: $red_color;
  font-weight: 700;
}

.table-form {
  table {
    td {
      &:last-child {
        span {
          white-space: nowrap;
        }
      }
    }
    .grid span { margin: 0 0 0 10px; }
  }
}

</style>
